// ---------------------------------------------------------------------------
// Funnel Service
// ---------------------------------------------------------------------------
// API client for Funnel Builder calculations.
// Follows profilesService pattern (object with async methods).
// ---------------------------------------------------------------------------

import { api } from '@/lib/api';
import type { DimensionType, UnitType } from '@/components/v11/funnel/types';

// ---------------------------------------------------------------------------
// Request Types
// ---------------------------------------------------------------------------

export interface FunnelLayerInput {
  readonly type: DimensionType;
  readonly dimension: string;
  readonly values: readonly (string | number)[];
}

// ---------------------------------------------------------------------------
// Response Types
// ---------------------------------------------------------------------------

export interface FunnelOriginResponse {
  readonly type: DimensionType;
  readonly count: number;
  readonly unit: UnitType;
}

export interface FunnelStepResponse {
  readonly index: number;
  readonly type: DimensionType;
  readonly dimension: string;
  readonly values: readonly (string | number)[];
  readonly count: number;
  readonly reductionPercent: number;
  readonly unit: UnitType;
  readonly previousCount: number;
}

export interface FunnelCalculateData {
  readonly origin: FunnelOriginResponse;
  readonly layers: readonly FunnelStepResponse[];
  readonly finalCount: number;
  readonly finalUnit: UnitType;
  readonly queryTimeMs: number;
}

export interface FunnelCalculateResponse {
  readonly success: boolean;
  readonly data: FunnelCalculateData;
}

// ---------------------------------------------------------------------------
// Compare Personas Types
// ---------------------------------------------------------------------------
// Note: Persona CRUD types moved to personaService.ts (unified).
// PersonaLayer type now imported from personaService.ts.
// ---------------------------------------------------------------------------

import type { PersonaLayer } from './personaService';

export interface PersonaComparisonMeta {
  readonly id: number;
  readonly name: string;
  readonly description: string | null;
  readonly layerCount: number;
  readonly layers: readonly PersonaLayer[];
}

export interface PersonaComparisonSections {
  readonly vanityMetrics: {
    readonly totalProfiles: number;
    readonly totalFollowers: number;
    readonly avgFollowers: number;
    readonly totalComments: number;
    readonly totalPosts: number;
  };
  readonly gender: { readonly male: number; readonly female: number; readonly other: number };
  readonly age: {
    readonly genAlpha: number;
    readonly genZ: number;
    readonly millennials: number;
    readonly genX: number;
    readonly boomers: number;
  };
  readonly ethnicity: readonly { name: string; count: number }[];
  readonly country: readonly { name: string; count: number }[];
  readonly lifestyle: readonly { name: string; count: number }[];
  readonly followerTier: {
    readonly nano: number;
    readonly micro: number;
    readonly mid: number;
    readonly macro: number;
    readonly mega: number;
  };
  readonly emotions: {
    readonly joy: number;
    readonly excitement: number;
    readonly admiration: number;
    readonly gratitude: number;
    readonly love: number;
    readonly pride: number;
    readonly hope: number;
    readonly sadness: number;
    readonly frustration: number;
    readonly fear: number;
    readonly surprise: number;
    readonly annoyance: number;
  };
  readonly sentiment: {
    readonly average: number;
    readonly positive: number;
    readonly neutral: number;
    readonly negative: number;
  };
  readonly platformMix: {
    readonly instagram: number;
    readonly tiktok: number;
    readonly x: number;
    readonly youtube: number;
  };
  readonly languages: readonly { name: string; count: number }[];
  readonly statusClass: readonly { name: string; count: number }[];
  readonly quality: {
    readonly isPerson: number;
    readonly verified: number;
    readonly famous: number;
    readonly spamCount: number;
    readonly toxicCount: number;
  };
  readonly teams: readonly { name: string; count: number }[];
  readonly hashtags: readonly { name: string; count: number }[];
}

export interface PersonaComparisonData {
  readonly meta: PersonaComparisonMeta;
  readonly sections: PersonaComparisonSections;
}

interface ComparePersonasResponse {
  readonly success: boolean;
  readonly data: {
    readonly personaA: PersonaComparisonData;
    readonly personaB: PersonaComparisonData;
  };
  readonly queryTimeMs: number;
}

// ---------------------------------------------------------------------------
// Brand/Team Search Types
// ---------------------------------------------------------------------------

export interface BrandSearchResult {
  readonly id: number;
  readonly name: string;
  readonly logo: string | null;
  readonly category: string | null;
}

interface BrandSearchResponse {
  readonly success: boolean;
  readonly data: readonly BrandSearchResult[];
}

// ---------------------------------------------------------------------------
// Post Search Types
// ---------------------------------------------------------------------------

export interface PostSearchResult {
  readonly id: number;
  readonly caption: string;
  readonly platform: string;
  readonly author: string;
  readonly likes: number;
}

interface PostSearchResponse {
  readonly success: boolean;
  readonly data: readonly PostSearchResult[];
}

// ---------------------------------------------------------------------------
// Hashtag Search Types
// ---------------------------------------------------------------------------

export interface HashtagSearchResult {
  readonly id: string;
  readonly name: string;
  readonly postsCount: number;
}

interface HashtagSearchResponse {
  readonly success: boolean;
  readonly data: readonly HashtagSearchResult[];
}

// ---------------------------------------------------------------------------
// Channel Search Types
// ---------------------------------------------------------------------------

export interface ChannelSearchResult {
  readonly id: number;
  readonly name: string;
  readonly platform: string;
  readonly postsCount: number;
  readonly totalLikes: number;
}

interface ChannelSearchResponse {
  readonly success: boolean;
  readonly data: readonly ChannelSearchResult[];
}

// ---------------------------------------------------------------------------
// Item Search Types
// ---------------------------------------------------------------------------

export interface ItemSearchResult {
  readonly id: number;
  readonly name: string;
  readonly icon: string | null;
}

interface ItemSearchResponse {
  readonly success: boolean;
  readonly data: readonly ItemSearchResult[];
}

// ---------------------------------------------------------------------------
// Dimension Counts Types
// ---------------------------------------------------------------------------

export interface DimensionCountsData {
  readonly audience: Readonly<Record<string, number>>;
  readonly content: Readonly<Record<string, number>>;
}

interface DimensionCountsResponse {
  readonly success: boolean;
  readonly data: DimensionCountsData;
  readonly queryTimeMs: number;
}

// ---------------------------------------------------------------------------
// Value Counts Types
// ---------------------------------------------------------------------------

export type ValueCountsData = Readonly<Record<string, number>>;

interface ValueCountsResponse {
  readonly success: boolean;
  readonly data: ValueCountsData;
  readonly queryTimeMs: number;
}

// ---------------------------------------------------------------------------
// Origin Count Types
// ---------------------------------------------------------------------------

export interface OriginCountData {
  readonly count: number;
  readonly unit: string;
}

interface OriginCountResponse {
  readonly success: boolean;
  readonly data: OriginCountData;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const funnelService = {
  /** Get origin count (total profiles for the authenticated account). Cached in Redis. */
  async getOriginCount(includeOwnChannels: boolean = false): Promise<OriginCountData> {
    const response = await api.get<OriginCountResponse>('/api/v11/discovery/funnel/origin-count', {
      params: { include_own_channels: includeOwnChannels ? 'true' : 'false' },
    });
    return (response as unknown as OriginCountResponse).data;
  },

  async calculate(layers: readonly FunnelLayerInput[], includeOwnChannels: boolean = false): Promise<FunnelCalculateResponse> {
    const response = await api.post<FunnelCalculateResponse>(
      '/api/v11/discovery/funnel/calculate',
      { layers, include_own_channels: includeOwnChannels ? 'true' : 'false' }
    );
    return response as unknown as FunnelCalculateResponse;
  },

  async getDimensionCounts(layers: readonly FunnelLayerInput[], includeOwnChannels: boolean = false): Promise<DimensionCountsData> {
    const response = await api.post<DimensionCountsResponse>(
      '/api/v11/discovery/funnel/dimension-counts',
      { layers, include_own_channels: includeOwnChannels ? 'true' : 'false' }
    );
    return (response as unknown as DimensionCountsResponse).data;
  },

  async getValueCounts(
    layers: readonly FunnelLayerInput[],
    dimensionType: DimensionType,
    dimensionKey: string,
    includeOwnChannels: boolean = false
  ): Promise<ValueCountsData> {
    const response = await api.post<ValueCountsResponse>('/api/v11/discovery/funnel/value-counts', {
      layers,
      dimensionType,
      dimensionKey,
      include_own_channels: includeOwnChannels ? 'true' : 'false',
    });
    return (response as unknown as ValueCountsResponse).data;
  },

  async searchBrands(query: string, limit = 30): Promise<readonly BrandSearchResult[]> {
    const response = await api.get<BrandSearchResponse>('/api/v11/discovery/brands/search', {
      params: { q: query, limit },
    });
    return (response as unknown as BrandSearchResponse)?.data ?? [];
  },

  async searchTeams(query: string, limit = 30): Promise<readonly BrandSearchResult[]> {
    const response = await api.get<BrandSearchResponse>('/api/v11/discovery/teams/search', {
      params: { q: query, limit },
    });
    return (response as unknown as BrandSearchResponse)?.data ?? [];
  },

  async searchPosts(
    query: string,
    limit = 30,
    dateRange?: { date_from?: string; date_to?: string }
  ): Promise<readonly PostSearchResult[]> {
    const response = await api.get<PostSearchResponse>('/api/v11/discovery/funnel/search-posts', {
      params: { q: query, limit, ...dateRange },
    });
    return (response as unknown as PostSearchResponse)?.data ?? [];
  },

  async searchHashtags(
    query: string,
    limit = 30,
    dateRange?: { date_from?: string; date_to?: string }
  ): Promise<readonly HashtagSearchResult[]> {
    const response = await api.get<HashtagSearchResponse>(
      '/api/v11/discovery/funnel/search-hashtags',
      { params: { q: query, limit, ...dateRange } }
    );
    return (response as unknown as HashtagSearchResponse)?.data ?? [];
  },

  async searchChannels(
    query: string,
    limit = 30,
    dateRange?: { date_from?: string; date_to?: string }
  ): Promise<readonly ChannelSearchResult[]> {
    const response = await api.get<ChannelSearchResponse>(
      '/api/v11/discovery/funnel/search-channels',
      { params: { q: query, limit, ...dateRange } }
    );
    return (response as unknown as ChannelSearchResponse)?.data ?? [];
  },

  async searchItems(query: string, limit = 30): Promise<readonly ItemSearchResult[]> {
    const response = await api.get<ItemSearchResponse>('/api/v11/discovery/items/search', {
      params: { q: query, limit },
    });
    return (response as unknown as ItemSearchResponse)?.data ?? [];
  },

  /**
   * Compare two personas side by side.
   *
   * Note: Persona CRUD methods (save, get, delete) moved to personaService.ts (unified).
   * This comparison method remains here as it's Funnel-specific.
   */
  async comparePersonas(
    personaIdA: number,
    personaIdB: number
  ): Promise<ComparePersonasResponse['data']> {
    const response = await api.post<ComparePersonasResponse>(
      '/api/v11/discovery/funnel/personas/compare',
      { personaIdA, personaIdB }
    );
    return (response as unknown as ComparePersonasResponse).data;
  },
};
